import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Card, Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import GradientButton from './GradientButton';

function EducationForm({ backlink, nextlink, title, subtitle }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    school_name: '',
    city: '',
    start_date: '',
    end_date: '',
    currentlyStudying: false,
    description: [],
    id: null,
  });

  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (location.state) {
      const loc = location.state;
      const currently =
        typeof loc.currentlyStudying === 'boolean'
          ? loc.currentlyStudying
          : !loc.end_date;

      setFormData({
        school_name: loc.school_name || '',
        city: loc.city || '',
        start_date: loc.start_date || '',
        end_date: loc.end_date || '',
        currentlyStudying: currently,
        description: loc.description || [],
        id: loc.id || null,
      });
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
        end_date: checked ? '' : prev.end_date,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  useEffect(() => {
    const { school_name, city, start_date, end_date, currentlyStudying } = formData;
    const isFormValid =
      (school_name || '').trim() !== '' &&
      (city || '').trim() !== '' &&
      (start_date || '').trim() !== '' &&
      ((end_date || '').trim() !== '' || currentlyStudying);
    setIsValid(isFormValid);
  }, [formData]);

  const handleNext = () => {
    if (!isValid) return;

    navigate(nextlink, {
      state: {
        ...formData,
        description: formData.description || [],
        id: formData.id || null,
      },
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '4rem',
      overflowX: 'clip',
      width: '100%'
    }}>
      <Container fluid className="px-2 px-md-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-4"
        >
          <h2 className="text-white fw-bold mb-2">
            {title}
          </h2>
          <p className="text-white-50">{subtitle}</p>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="row justify-content-center"
        >
          <div className="col-12 col-lg-10 col-xl-8">
            <Card className="shadow-lg border-0" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <Card.Header style={{
                background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                border: 'none',
                padding: '1.5rem'
              }}>
                <h5 className="text-white mb-0">
                  <FaGraduationCap className="me-2" />
                  Informacje o edukacji
                </h5>
              </Card.Header>
              
              <Card.Body style={{ padding: '2rem' }}>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Nazwa szkoły</Form.Label>
                    <Form.Control
                      name="school_name"
                      type="text"
                      placeholder="np. Zespół Szkół Technicznych nr 1"
                      value={formData.school_name}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                      className="py-2"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Miasto</Form.Label>
                    <Form.Control
                      name="city"
                      type="text"
                      placeholder="np. Warszawa"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                      className="py-2"
                    />
                  </Form.Group>

                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-medium">Data rozpoczęcia</Form.Label>
                        <Form.Control
                          name="start_date"
                          type="month"
                          value={formData.start_date}
                          onChange={handleChange}
                          required
                          style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                          className="py-2"
                        />
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-medium">Data zakończenia</Form.Label>
                        <Form.Control
                          name="end_date"
                          type="month"
                          value={formData.end_date}
                          onChange={handleChange}
                          disabled={formData.currentlyStudying}
                          style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                          className="py-2"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="form-check mb-4" style={{ cursor: 'pointer' }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="currentlyStudying"
                      name="currentlyStudying"
                      checked={formData.currentlyStudying}
                      onChange={handleChange}
                      style={{ cursor: 'pointer' }}
                    />
                    <label
                      className="form-check-label fw-medium"
                      htmlFor="currentlyStudying"
                      style={{ cursor: 'pointer' }}
                    >
                      Obecnie się tu uczę
                    </label>
                  </div>
                </Form>
              </Card.Body>
            </Card>
            {/* Buttons */}
            <div className="row g-3 mt-2">
              <div className="d-flex justify-content-between">
                <GradientButton                      
                  onClick={() => navigate(backlink)}
                  gradient="pink"
                >
                  <FaArrowLeft className="me-2" />
                  Wstecz
                </GradientButton>
                <GradientButton
                  onClick={handleNext}
                  disabled={!isValid}
                  gradient="blue"
                >
                  Dalej
                  <FaArrowRight className="ms-2" />
                </GradientButton>
              </div>
            </div>
          </div>
        </motion.div>
      </Container>
    </div>
  );
}

export default EducationForm;